/**
 * Send action & messaging — the platform's transport (`/api/messaging`).
 *
 * The browser sends ids and a template; the server re-reads the send binding
 * by the action's name, the records through the engine as the caller, and
 * resolves every recipient from the template and the data. Run-as headers
 * (a project user in the Run view) are attached by the shared axios client.
 *
 * The window, composer, admin console and log are shared with the downloaded
 * runtime and J2EE (`@twasta/page-runtime/messaging`); this is the platform's
 * implementation of their `MessagingTransport`.
 */
import api from './client'
import type {
  BindingInfo, ChannelKey, ChannelSettings, ChannelsAdmin, ConnectResult, MessageItem, MessageTemplate,
  MessagingTransport, PreviewRequest, PreviewResponse, ProvisionResult, RunDetail, RunSummary, SendResponse,
  TenantSendObject, TenantSendPatch, TenantSendState, TenantTemplates,
} from '@twasta/page-runtime/messaging'
import { sendErrorInfo } from '@twasta/page-runtime/messaging'

const enc = encodeURIComponent

function asTemplate(data: unknown): MessageTemplate {
  const d = data as { template?: MessageTemplate } & MessageTemplate
  return (d && typeof d === 'object' && d.template) ? d.template : d
}

function asTemplates(data: unknown): MessageTemplate[] {
  if (Array.isArray(data)) return data as MessageTemplate[]
  const d = data as { templates?: MessageTemplate[] } | null
  return Array.isArray(d?.templates) ? d!.templates! : []
}

/** The platform transport for one project. */
export function platformMessagingTransport(project: string): MessagingTransport {
  const base = `/messaging/${enc(project)}`
  return {
    async binding(obj, action) {
      const res = await api.get(`${base}/binding`, { params: { obj, action } })
      return res.data as BindingInfo
    },
    async templates(obj, action) {
      const res = await api.get(`${base}/templates`, { params: { obj, action } })
      return asTemplates(res.data)
    },
    async saveTemplate(obj, action, template, scope) {
      const res = await api.post(`${base}/templates`, { obj, action, template, scope })
      return asTemplate(res.data)
    },
    async deleteTemplate(obj, action, templateId, scope) {
      await api.delete(`${base}/templates`, { params: { obj, action, template_id: templateId, scope } })
    },
    async preview(req: PreviewRequest) {
      const res = await api.post(`${base}/preview`, req)
      return res.data as PreviewResponse
    },
    async send(req: PreviewRequest) {
      const res = await api.post(`${base}/send`, req)
      return res.data as SendResponse
    },
    async runs(obj, txnId, limit) {
      const res = await api.get(`${base}/runs`, {
        params: { obj, ...(txnId ? { txn_id: txnId } : {}), ...(limit ? { limit } : {}) },
      })
      return res.data as { runs?: RunSummary[]; items?: MessageItem[] }
    },
    async run(obj, runKey) {
      const res = await api.get(`${base}/runs/${enc(runKey)}`, { params: { obj } })
      return res.data as RunDetail
    },
    async retry(obj, runKey) {
      const res = await api.post(`${base}/runs/${enc(runKey)}/retry`, { obj }, { params: { obj } })
      return res.data as { retried: number }
    },
    async adminSends(obj?: string) {
      const res = await api.get(`${base}/admin/sends`, { params: obj ? { obj } : {} })
      return res.data as TenantSendState | TenantSendObject
    },
    async saveSend(obj: string, bindingId: string, patch: TenantSendPatch) {
      const res = await api.put(`${base}/admin/sends/${enc(obj)}/${enc(bindingId)}`, patch)
      return res.data as TenantSendObject
    },
    async resetSend(obj: string, bindingId: string) {
      const res = await api.delete(`${base}/admin/sends/${enc(obj)}/${enc(bindingId)}`)
      return res.data as TenantSendObject
    },
    async adminTemplates(obj: string) {
      const res = await api.get(`${base}/admin/templates`, { params: { obj } })
      return res.data as TenantTemplates
    },
    async adminBinding(obj: string, bindingId: string) {
      const res = await api.get(`${base}/admin/binding`, { params: { obj, binding_id: bindingId } })
      return res.data as BindingInfo
    },
    async saveAdminTemplate(obj: string, bindingId: string, scope: 'override' | 'shared', template: MessageTemplate) {
      const res = await api.put(`${base}/admin/templates`, { obj, binding_id: bindingId, scope, template })
      return asTemplate(res.data) || template
    },
    async deleteAdminTemplate(obj: string, templateId: string, scope: 'override' | 'shared') {
      await api.delete(`${base}/admin/templates`, { params: { obj, template_id: templateId, scope } })
    },
    async channels() {
      const res = await api.get(`${base}/admin/channels`)
      return res.data as ChannelsAdmin
    },
    async saveChannels(settings: ChannelSettings) {
      const res = await api.put(`${base}/admin/channels`, settings)
      return res.data as ChannelsAdmin
    },
    async connectChannel(body: { app_id: string; channel: ChannelKey; auth_credentials?: Record<string, string> }) {
      const res = await api.post(`${base}/admin/channels/connect`, body)
      return res.data as ConnectResult
    },
    async testChannel(channel: ChannelKey, address: string) {
      const res = await api.post(`${base}/admin/channels/test`, { channel, address })
      return res.data as { ok: boolean; message_id?: string | null; error?: string | null }
    },
    async provision() {
      const res = await api.post(`${base}/admin/channels/provision`, {})
      return res.data as ProvisionResult
    },
    describeError: sendErrorInfo,
  }
}

// ── designer: STARTER templates (Metadata_Model/message_templates/<obj>/) ──

export async function listStarterTemplates(project: string, obj: string): Promise<MessageTemplate[]> {
  const res = await api.get('/designers/message-templates', { params: { project, obj } })
  return asTemplates(res.data)
}

export async function saveStarterTemplate(project: string, obj: string, template: MessageTemplate): Promise<MessageTemplate> {
  const res = await api.post('/designers/message-template', { project, obj_name: obj, template })
  return asTemplate(res.data) || template
}

export async function deleteStarterTemplate(project: string, obj: string, id: string): Promise<void> {
  await api.delete('/designers/message-template', { params: { project, obj, id } })
}
